import React, { useEffect, useState, useContext } from "react";
import { Box, Button, TextField, InputLabel, FormControl, Input, FormHelperText, Alert } from "@mui/material";
import * as Yup from 'yup';
import { apiGet, apiPost } from "app/Services/ApiService";
import { apiUrls } from "common/ApiUrls";
import TableAllData from "../CustomTable/TableAllData";
import AximDropdown from "components/Dropdown/AximDropdown";
import { PrivateRoutesContext } from "app/PrivateRoutesContext";

const emptyForm = {
    firstName: "",
    middleName: "",
    lastName: "",
    ssn: "",
    birthDate: "",
    genderID: 0,
    relationshipID: "",
};

const validationSchema = Yup.object().shape({
    firstName: Yup.string().trim().min(2, 'First name must be at least 2 characters').max(75, 'First name must not exceed 75 characters').required('First name is required'),
    lastName: Yup.string().trim().min(2, 'Last name must be at least 2 characters').max(75, 'Last name must not exceed 75 characters').required('Last name is required'),
    middleName: Yup.string().trim().max(75, 'Middle name must not exceed 75 characters'),
    ssn: Yup.string()
        .trim()
        .test('ssn-format', 'SSN must be 9 digits, formatted as XXX-XX-XXXX or without dashes', value => {
            if (!value) return true;
            const digitsOnly = value.replace(/-/g, '');
            if (!/^\d{9}$/.test(digitsOnly)) return false;
            return value.includes('-') ? /^\d{3}-\d{2}-\d{4}$/.test(value) : true;
        }).required('SSN is required'),
    birthDate: Yup.date()
        .min('1901-01-01', 'Birth date must be after 1901-01-01')
        .max(new Date(), 'Birth date cannot be in the future')
        .typeError('Birth date must be a valid date')
        .required('Birth date is required'),
    genderID: Yup.mixed().required('Gender is required'),
    relationshipID: Yup.mixed().required('Relationship is required'),
});

const keysChangeCase = (obj, toUpper = true) => Object.fromEntries(
    Object.entries(obj).map(([k, v]) => [(toUpper ? k.charAt(0).toUpperCase() : k.charAt(0).toLowerCase()) + k.slice(1), v])
);

function Dependents({ insuredId }) {

    const { companyId } = useContext(PrivateRoutesContext);
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState(emptyForm);
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState("");

    const [genderPairs, setGenderPairs] = useState([]);
    const [relationshipPairs, setRelationshipPairs] = useState([]);

    useEffect(() => {

        apiGet(apiUrls.getGenders())
            .then(response => {
                const pairs = (response.data || []).map(g => ({ value: g.genderId, text: g.genderName }));
                setGenderPairs(pairs);
            })
            .catch(() => setGenderPairs([]));

        apiGet(apiUrls.getRelationships())
            .then(response => {
                const pairs = (response.data || []).map(r => ({ value: r.relationshipId, text: r.relationshipName }));
                setRelationshipPairs(pairs);
            })
            .catch(() => setRelationshipPairs([]));

    }, []);

    useEffect(() => {

        if (!insuredId) return;

        setIsLoading(true);

        apiGet(apiUrls.getEmployeeDependents(insuredId, companyId))
            .then((response) => {

                const raw = response.data || [];


                const formatted = raw.map(d => ({
                    ...d,

                    fullName: `${d.firstName || ""} ${d.middleName || ""} ${d.lastName || ""}`
                        .replace(/\s+/g, " ")
                        .trim(),

                    relationship: relationshipPairs.find(r => r.value === d.relationshipID)?.text || "",

                    birthDate: d.birthDate
                        ? d.birthDate.split("T")[0]
                        : "",

                    gender: genderPairs.find(g => g.value === d.genderID)?.text || ""
                }));
                setData(formatted);
                setIsLoading(false);
            })
            .catch(() => {
                setData([]);
                setIsLoading(false);
            });

    }, [insuredId, companyId, refreshTrigger, genderPairs, relationshipPairs]);

    const handleChange = (name, value) => {
        setFormData(formData => ({ ...formData, [name]: value }));
    };

    const handleSsnChange = (value) => {
        const allowed = value.replace(/[^\d-]/g, '');

        let digitCount = 0;
        let capped = '';
        for (const ch of allowed) {
            if (ch === '-') {
                capped += ch;
            }
            else if (digitCount < 9) {
                capped += ch;
                digitCount++;
            }
        }

        handleChange('ssn', capped);
    };

    const handleAddClick = () => {
        setFormData(emptyForm);
        setErrors({});
        setSaveError("");
        setShowForm(true);
    };

    const handleCancel = () => {
        setShowForm(false);
        setErrors({});
        setSaveError("");
    };

    const handleSave = async () => {
        setSaveError("");

        try {
            await validationSchema.validate(formData, { abortEarly: false });
        }
        catch (validationErrors) {
            const fieldErrors = {};
            for (const err of validationErrors.inner) {
                if (err.path && !fieldErrors[err.path]) {
                    fieldErrors[err.path] = err.message;
                }
            }
            setErrors(fieldErrors);
            return;
        }

        setErrors({});
        setIsSaving(true);

        const newDependent = {
            insuredId,
            firstName: formData.firstName.trim(),
            middleName: formData.middleName.trim(),
            lastName: formData.lastName.trim(),
            ssn: formData.ssn.trim(),
            birthDate: formData.birthDate,
            genderID: formData.genderID,
            relationshipID: formData.relationshipID,
        };

        apiPost(apiUrls.saveDependent(), keysChangeCase(newDependent, true))
            .then(() => {
                setIsSaving(false);
                setShowForm(false);
                setRefreshTrigger(t => t + 1);
            })
            .catch((error) => {
                setIsSaving(false);
                setSaveError(error?.response?.data?.message || error?.response?.data || "Failed to save dependent. Please try again.");
            });
    };

    // =========================
    // TABLE COLUMNS
    // =========================
    const columns = [
        {
            Header: "Name",
            accessor: "fullName"
        },
        {
            Header: "SSN",
            accessor: "ssn"
        },
        {
            Header: "Relationship",
            accessor: "relationship"
        },
        {
            Header: "Birth Date",
            accessor: "birthDate"
        },
        {
            Header: "Gender",
            accessor: "gender"
        }
    ];

    return (
        <Box className="container-fluid">

            <Box className="d-flex justify-content-end mb-2">
                {!showForm &&
                    <Button variant="contained" onClick={handleAddClick} disabled={!insuredId}>Add Dependent</Button>
                }
            </Box>

            {showForm &&
                <Box className="row border rounded p-3 mb-3">
                    <Box className="col-12 col-md-4 mb-2">
                        <TextField
                            fullWidth
                            variant="standard"
                            label="First Name"
                            value={formData.firstName}
                            onChange={e => handleChange('firstName', e.target.value)}
                            error={!!errors.firstName}
                            helperText={errors.firstName}
                        />
                    </Box>
                    <Box className="col-12 col-md-4 mb-2">
                        <TextField
                            fullWidth
                            variant="standard"
                            label="Middle Name (Optional)"
                            value={formData.middleName}
                            onChange={e => handleChange('middleName', e.target.value)}
                            error={!!errors.middleName}
                            helperText={errors.middleName}
                        />
                    </Box>
                    <Box className="col-12 col-md-4 mb-2">
                        <TextField
                            fullWidth
                            variant="standard"
                            label="Last Name"
                            value={formData.lastName}
                            onChange={e => handleChange('lastName', e.target.value)}
                            error={!!errors.lastName}
                            helperText={errors.lastName}
                        />
                    </Box>
                    <Box className="col-12 col-md-4 mb-2">
                        <TextField
                            fullWidth
                            variant="standard"
                            label="SSN"
                            placeholder="XXX-XX-XXXX"
                            value={formData.ssn}
                            onChange={e => handleSsnChange(e.target.value)}
                            inputProps={{ maxLength: 11 }}
                            error={!!errors.ssn}
                            helperText={errors.ssn}
                        />
                    </Box>
                    <Box className="col-12 col-md-4 mb-2">
                        <FormControl fullWidth error={!!errors.birthDate}>
                            <InputLabel shrink>Birth Date</InputLabel>
                            <Input
                                type="date"
                                value={formData.birthDate}
                                onChange={e => handleChange('birthDate', e.target.value)}
                            />
                            <FormHelperText>{errors.birthDate}</FormHelperText>
                        </FormControl>
                    </Box>
                    <Box className="col-12 col-md-4 mb-2">
                        <AximDropdown
                            id="dependent-genderID"
                            label="Gender"
                            value={formData.genderID}
                            handleChange={e => handleChange('genderID', e.target.value)}
                            pairs={genderPairs}
                        />
                        {errors.genderID &&
                            <FormHelperText error>{errors.genderID}</FormHelperText>
                        }
                    </Box>
                    <Box className="col-12 col-md-4 mb-2">
                        <AximDropdown
                            id="dependent-relationshipID"
                            label="Relationship"
                            value={formData.relationshipID}
                            handleChange={e => handleChange('relationshipID', e.target.value)}
                            pairs={relationshipPairs}
                        />
                        {errors.relationshipID &&
                            <FormHelperText error>{errors.relationshipID}</FormHelperText>
                        }
                    </Box>

                    {saveError &&
                        <Box className="col-12 mb-2">
                            <Alert severity="error">{saveError}</Alert>
                        </Box>
                    }

                    <Box className="col-12 d-flex justify-content-end">
                        <Button onClick={handleCancel} disabled={isSaving} className="me-2">Cancel</Button>
                        <Button variant="contained" onClick={handleSave} disabled={isSaving}>
                            {isSaving ? "Saving..." : "Save"}
                        </Button>
                    </Box>
                </Box>
            }

            <TableAllData
                key={`dependents-${insuredId}`}
                columns={columns}
                data={data}
                isLoading={isLoading}
            />

        </Box>
    );
}

export default Dependents;
